import axios, {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig,
  AxiosResponse,
  CreateAxiosDefaults,
} from "axios";
import { LocalStorageKeys, env } from "../constants";

type TRefreshTokenRes = {
  accessToken: string;
};

type TFailedQueueItem = {
  resolve: (token: string) => void;
  reject: (error: AxiosError) => void;
};

const CONFIG: CreateAxiosDefaults = {
  baseURL: env.API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 1000 * 60, // 1 minute
};

const AUTH_BYPASS_ALLOWLIST = [
  "/Account/Register",
  "/Account/Login",
  "/Account/ForgotPassword",
  "/Account/ResetPassword",
  "/Account/VerifyEmail",
  "/Account/ResendEmailVerification",
].map((path) => path.toLowerCase());

// Main and default axios instance for API requests
export const axiosInstance: AxiosInstance = axios.create(CONFIG);

/*
 * This prevents recursion if the refresh endpoint itself returns 401,
 * which would cause an infinite loop
 */
const refreshInstance: AxiosInstance = axios.create(CONFIG);

// Singleton flag to indicate if a token refresh is in progress
let isRefreshing = false;

// Queue for all failed requests while refreshing the token
let failedQueue: TFailedQueueItem[] = [];

/*
 * Processes the queue of failed requests:
 * If the refresh was successful, retry them with the new token.
 * If the refresh failed, reject them.
 */
const processQueue = (error: AxiosError | null, token: string | null) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else if (token) {
      promise.resolve(token);
    }
  });
  failedQueue = [];
};

// Attach access token to every outgoing request if it exists.
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(LocalStorageKeys.ACCESS_TOKEN);
    // const apiPassword = localStorage.getItem(LocalStorageKeys.API_PASSWORD);
    // const isExtension =
    //   localStorage.getItem(LocalStorageKeys.IS_EXTENSION) === "true";
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // if (env.APP_ENV !== "production") {
    //   config.headers["X-Api-Password"] = isExtension ? process.env.REACT_APP_API_PASSWORD : apiPassword;
    // }
    return config;
  },
  (error) => Promise.reject(error),
);

// Handle 401 errors by attempting to refresh the token.
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError): Promise<AxiosResponse | Promise<never>> => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    if (
      (error.response?.data as any)?.ErrorMessage === "No active subscription found."
    ) {
      window.dispatchEvent(new CustomEvent("subscription-expired"));
      return Promise.reject(error);
    }

    if (
      error.response?.status === 403 &&
      error.response?.data === "Forbidden: Invalid password."
    ) {
      localStorage.removeItem(LocalStorageKeys.API_PASSWORD);
      window.location.reload();
    }

    /*
     * Do not redirect on 401 for auth attempts (e.g., wrong credentials).
     * Possibly better to do this on the back-end side (e.g., send different status codes)
     */
    const { pathname } = new URL(originalRequest?.url ?? "", env.API_BASE_URL);

    if (
      error.response?.status === 401 &&
      AUTH_BYPASS_ALLOWLIST.includes(pathname.toLowerCase())
    ) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      // Mark this request so it doesn't cause an infinite loop
      originalRequest._retry = true;

      if (isRefreshing) {
        // If refresh is already happening, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token: string) => {
              // Once the new token is available, retry the request
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(axiosInstance(originalRequest));
            },
            reject: (err: AxiosError) => {
              reject(err);
            },
          });
        });
      }

      // Start the refresh process
      isRefreshing = true;

      const refreshToken = localStorage.getItem(LocalStorageKeys.REFRESH_TOKEN);
      const isExtension =
        localStorage.getItem(LocalStorageKeys.IS_EXTENSION) === "true";

      // No refresh token, force logout
      if (!refreshToken) {
        localStorage.removeItem(LocalStorageKeys.ACCESS_TOKEN);
        localStorage.removeItem(LocalStorageKeys.REFRESH_TOKEN);
        if (isExtension) {
          chrome?.storage?.local?.remove?.(["access_token", "refresh_token"]);
          window.location.reload();
        } else {
          window.location.assign("/login");
        }
        return Promise.reject(error);
      }

      try {
        const refreshRes = await refreshInstance.post<TRefreshTokenRes>(
          "/account/refresh-token",
          { refreshToken },
        );
        const newAccessToken = refreshRes.data.accessToken;

        // Store the new access token
        localStorage.setItem(LocalStorageKeys.ACCESS_TOKEN, newAccessToken);

        // Update the default header for future requests
        axiosInstance.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`;

        // Retry all queued requests with the new token
        processQueue(null, newAccessToken);

        // Retry the original request with the new token
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return axiosInstance(originalRequest);
      } catch (e) {
        // If the refresh fails, reject all queued requests and log out
        processQueue(e as AxiosError, null);
        localStorage.removeItem(LocalStorageKeys.ACCESS_TOKEN);
        localStorage.removeItem(LocalStorageKeys.REFRESH_TOKEN);
        if (isExtension) {
          chrome?.storage?.local?.remove?.(["access_token", "refresh_token"]);
          window.location.reload();
        } else {
          window.location.assign("/login");
        }
        return Promise.reject(e);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);
